/**
 * TripShapeCard — replaces the dense "Destination Direction" paragraph
 * with a structured At-a-Glance card. (Wave 1 · Priority 2)
 *
 * Rows: Dates · Portugal Base · Greece Base · Pace · Style · Budget
 * Target · Flights/Routing.
 *
 * Reads the trip + design_brief context that's already on the page
 * (passed in via `shape`) — no extra fetch. The advisor can edit
 * the underlying fields in Design Studio.
 */
import React from "react";
import { Calendar, MapPin, Activity, Compass, DollarSign, Plane, Sparkles } from "lucide-react";
import { Surface } from "../index";

const ICONS = {
  dates: Calendar,
  portugal_base: MapPin,
  greece_base: MapPin,
  pace: Activity,
  style: Compass,
  budget_target: DollarSign,
  flights: Plane,
  fallback: Sparkles,
};

export default function TripShapeCard({ shape, testIdBase = "client-tripshape" }) {
  if (!shape || typeof shape !== "object") return null;
  const rows = (shape.rows || []).filter((r) => r && r.value);
  if (rows.length === 0) return null;
  return (
    <Surface
      variant="paper"
      padded
      className="!p-5 sm:!p-6"
      data-testid={`${testIdBase}-card`}
    >
      <div className="mb-4 pb-3 border-b border-[rgba(0,0,0,0.05)]">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]">
          At a glance
        </p>
        {shape.headline && (
          <h3 className="mt-1 font-serif text-[19px] sm:text-[21px] leading-tight text-[hsl(var(--foreground))]">
            {shape.headline}
          </h3>
        )}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {rows.map((r) => {
          const Icon = ICONS[r.key] || ICONS.fallback;
          return (
            <div
              key={r.key}
              className="flex items-baseline gap-3 py-1.5 border-b border-[rgba(0,0,0,0.04)] last:border-b-0 sm:border-b-0"
              data-testid={`${testIdBase}-row-${r.key}`}
            >
              <Icon
                className="size-3 text-[hsl(var(--foreground-muted))] shrink-0 self-center"
                strokeWidth={1.5}
              />
              <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] w-28 shrink-0">
                {r.label}
              </dt>
              <dd className="font-serif text-[14px] sm:text-[14.5px] leading-snug text-[hsl(var(--foreground))] flex-1">
                {r.value}
              </dd>
            </div>
          );
        })}
      </dl>
      {shape.footnote && (
        <p className="mt-4 pt-3 border-t border-[rgba(0,0,0,0.05)] font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] leading-relaxed">
          {shape.footnote}
        </p>
      )}
    </Surface>
  );
}

/**
 * Best-effort shape-rows builder from a proposal payload + raw items.
 * Looks up cities from hotel items, dates from trip metadata, budget
 * tier text, etc. Falls back gracefully to whatever data is present.
 */
export function buildTripShapeRows({ proposal, items, trip }) {
  if (!proposal) return [];

  const safeDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };
  const dates =
    proposal.trip_dates ||
    (proposal.start_date && proposal.end_date
      ? `${safeDate(proposal.start_date)} → ${safeDate(proposal.end_date)}`
      : trip?.start_date && trip?.end_date
        ? `${safeDate(trip.start_date)} → ${safeDate(trip.end_date)}`
        : null);

  const hotels = (items || []).filter((it) => it.category === "hotel");
  const cityOf = (h) => h.city || h.destination || null;
  // Heuristic: pick the first "Portugal" hotel + first "Greece" hotel.
  const portugalHotel = hotels.find((h) => /portug|cascais|lisb|sintra|porto|algar/i.test(`${h.country || ""} ${h.city || ""}`));
  const greeceHotel = hotels.find((h) => /greec|athens|santor|crete|mykono/i.test(`${h.country || ""} ${h.city || ""}`));

  const shape = proposal.trip_shape || {};

  const rows = [
    { key: "dates", label: "Dates", value: dates || shape.dates },
    {
      key: "portugal_base",
      label: "Portugal Base",
      value:
        shape.portugal_base ||
        (portugalHotel ? `${cityOf(portugalHotel) || "Portugal"} · ${portugalHotel.title}` : null),
    },
    {
      key: "greece_base",
      label: "Greece Base",
      value:
        shape.greece_base ||
        (greeceHotel ? `${cityOf(greeceHotel) || "Greece"} · ${greeceHotel.title}` : null),
    },
    { key: "pace", label: "Pace", value: shape.pace || proposal.pace || null },
    { key: "style", label: "Style", value: shape.style || proposal.style || null },
    {
      key: "budget_target",
      label: "Budget Target",
      value: shape.budget_target || proposal.budget_target || null,
    },
    {
      key: "flights",
      label: "Flights / Routing",
      value: shape.flights || proposal.flights || null,
    },
  ];
  return rows.filter((r) => r.value);
}
